import type {
  EntityDetail,
  AnnotatedJsonSchema,
  EntityDetailSchemaWithRelationships,
  EntityDetailSchemaWithoutRelationships,
} from '@web-config-app/core';
import jp from 'jsonpath';
import {
  computeSchema,
  isRelationshipEntityReference,
} from '@web-config-app/schema-utils';
import type { FormError } from '../../types';

function schemaPathToJsonPath(schemaPath: string | undefined) {
  if (!schemaPath || typeof schemaPath !== 'string') {
    return '$';
  }

  const parts = schemaPath.split('.');
  const jsonPathParts = ['$'];

  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];

    if (part === 'properties') {
      // Skip 'properties' - the next part will be the actual property name
    } else if (part === 'items') {
      // 'items' indicates an array, so add [*] to the previous property
      if (jsonPathParts.length > 1) {
        jsonPathParts[jsonPathParts.length - 1] += '[*]';
      }
    } else if (part === 'oneOf' || part === 'anyOf' || part === 'allOf') {
      // Skip schema composition keywords
    } else {
      // Regular property name
      jsonPathParts.push(part);
    }
  }

  return jsonPathParts.join('.');
}

type SchemaParam =
  | EntityDetailSchemaWithRelationships
  | EntityDetailSchemaWithoutRelationships
  | AnnotatedJsonSchema;

const isEntityDetailsSchemaWithRelationships = (
  schema: SchemaParam,
): schema is EntityDetailSchemaWithRelationships =>
  !!schema.properties?.relationships &&
  !!schema.properties?.relationships.properties;

export const validateRelationships = (
  schema: SchemaParam,
  data: EntityDetail | undefined,
): FormError[] => {
  const relationships = data?.relationships ?? [];
  if (
    !isEntityDetailsSchemaWithRelationships(schema) ||
    relationships.length === 0
  ) {
    return [];
  }

  const relationshipsSchema = schema.properties.relationships;
  const formErrors: FormError[] = [];

  Object.entries(relationshipsSchema?.properties ?? {}).forEach(
    ([relationshipName, relationshipSchema]: [string, AnnotatedJsonSchema]) => {
      let relationshipSchemaPath: string | undefined;
      let relationshipPropertySchema: AnnotatedJsonSchema | undefined;

      computeSchema(schema, data, [
        ({ schema: propertySchema, options }) => {
          const { 'x-entity-reference': entityReference } = propertySchema;
          if (
            isRelationshipEntityReference(entityReference) &&
            entityReference.relationship === relationshipName
          ) {
            const { computePath } = options ?? {};

            relationshipSchemaPath = computePath;
            relationshipPropertySchema = propertySchema;
          }

          return propertySchema;
        },
      ]);

      // We only need to do this validation against array relationships in instances
      // where the related entity ID is stored in a string property in addition to
      // the relationship data.

      if (
        relationshipSchemaPath &&
        relationshipSchema.properties?.data?.type === 'array' &&
        relationshipPropertySchema?.type === 'string'
      ) {
        const jsonPath = schemaPathToJsonPath(relationshipSchemaPath);
        const referencePropertyData = jp.nodes(data, jsonPath);

        referencePropertyData.forEach((node) => {
          const referenceDataId = node.value;
          const relationshipData =
            data?.relationships?.[relationshipName]?.data;

          // We want to create an error in the case where a property holds an ID reference that is
          // not present in the relationship data.
          if (
            referenceDataId &&
            Array.isArray(relationshipData) &&
            !relationshipData.find(({ id }) => id === referenceDataId)
          ) {
            const propertyName =
              relationshipPropertySchema?.['x-entity-label']?.key;

            formErrors.push({
              instancePath: node.path.join('/').replace('$', ''),
              propertyName:
                typeof propertyName === 'string' ? propertyName : undefined,
              schemaPath: `#/${relationshipSchemaPath}`.replaceAll('.', '/'),
              keyword: 'relationship',
              message: `broken relationship for ID ${referenceDataId}`,
              params: {
                relationshipName,
                referenceDataId,
                schema: relationshipSchema,
              },
            });
          }
        });
      }
    },
  );

  return formErrors;
};
